<template>
  <div class="map">
    <div class="map__wrapper">
      <div class="map__map-wrapper">
        <SearchButton
          :formWrapper="formWrapper"
          @openModal="modal = true"
        />

        <ClientOnly>
          <DmModal
            v-model="modal"
            title="Usługa lub warsztat"
          >
            <MapSearchModal
              @close="modal = false"
              :formWrapper="formWrapper"
              :foundServices="foundServicesModal"
              :foundWorkshops="foundWorkshopsModal"
              :loading="loading"
              :typing="typing"
            />
          </DmModal>
        </ClientOnly>
        <Transition>
          <div
            v-if="searchAreaButton"
            class="map__search-this-area"
            :class="{ 'map__search-this-area--down': !!formWrapper.form.value.fields.filters.brand }"
          >
            <DmButton
              @click="searchThisArea(true)"
              title="Wyszukaj w okolicy"
            />
          </div>
        </Transition>
        <div id="map" />
      </div>

      <div class="map__list-wrapper">
        <WorkshopsList
          :loading="loading || typing"
          :searchInitLoad="searchInitLoad"
          :workshops="foundWorkshops"
          :currentWorkshop="currentWorkshop"
          @show-workshop="showWorkshop($event)"
          @show-on-map="showOnMap($event)"
          @mouse-over="onMouseOver($event)"
          @mouse-leave="onMouseLeave($event)"
          @reset-current-workshop="currentWorkshop = null"
        />
      </div>
    </div>
  </div>
  <DmLoader
    v-if="loading || typing"
    icon-size="icon-lg"
  />
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader';
import SearchButton from '@/components/Map/SearchButton.vue';
import { homepageApi } from '@/api/homepage-instance';
import WorkshopsList from '@/components/Map/WorkshopsList.vue';
import { DmModal, DmButton, DmLoader } from '@dobrymechanik/vue-ui-v2';
import { convertMapZoomToMeters } from '@/helpers/helpers';
import debounce from 'debounce';
import MapSearchModal from '@/components/Map/MapSearchModal.vue';
import { useStore } from '~/store/store';

export interface IMapSearchFormFields {
  search: string;
  filters: {
    service: {
      id: string;
      name: string;
    } | null;
    brand: {
      id: string;
      name: string;
    } | null;
  };
}

export interface IWorkshop {
  id: number;
  name: string;
  slug_name: string;
  city: string;
  slug_city: string;
  quarter: string;
  slug_quarter: string;
  address: string;
  photo: string;
  index: number;
}

export interface IWorkshopExtended extends IWorkshop {
  location?: {
    lat: number;
    lon: number;
  };
  open_hours?: any;
  rating_total?: number;
  comments_count?: number;
  last_comment?: any;
  promoted_service?: {
    name: string;
    price_from: string;
    price_to: string;
  };
}

definePageMeta({
  layout: "only-header",
});

useSeoMeta({
  title: () => 'Znajdź mechanika na mapie • Umów wizytę w warsztacie • DobryMechanik.pl',
  description: () => 'Znajdź dobrego mechanika samochodowego. Sprawdź opinie, umów się szybko i wygodnie do sprawdzonego warsztatu samochodowego w Twojej okolicy.',
  ogTitle: () => 'Znajdź mechanika na mapie • Umów wizytę w warsztacie • DobryMechanik.pl',
  ogDescription: () => 'Znajdź dobrego mechanika samochodowego. Sprawdź opinie, umów się szybko i wygodnie do sprawdzonego warsztatu samochodowego w Twojej okolicy.',
  ogType: () => 'article',
  ogImage: () => 'https://dobrymechanik.pl/img/banners/og_image.jpg',
  ogSiteName: () => 'dobrymechanik.pl',
  ogUrl: () => 'https://dobrymechanik.pl/mapa'
});

useHead({
  meta: [
    { hid: 'robots', name: 'robots', content: 'noindex, nofollow' }
  ],
  link: [
    { rel: 'preconnect', href: '//cookie-cdn.cookiepro.com' },
    { rel: 'preconnect', href: '//googletagmanager.com' },
    { rel: 'preconnect', href: '//www.google-analytics.com' },
    { rel: 'preconnect', href: '//eu.posthog.com' },
    { rel: 'preload', href: 'https://googletagmanager.com/gtm.js?id=GTM-TTC4DXZ', as: 'script' },
    { rel: 'preload', href: 'https://google-analytics.com/analytics.js', as: 'script' }
  ],

});


// data
let map: google.maps.Map;
let markers: google.maps.marker.AdvancedMarkerElement[] = [];
let circle: google.maps.Circle | undefined;

const loader = new Loader({
  apiKey: 'AIzaSyD1bhbG0papd9q_7_PJtygtJzNqit1eqO8',
  libraries: ["maps"]
});

// reactive
const modal = ref(false);
const foundWorkshops: Ref<IWorkshopExtended[]> = ref([]);
const foundWorkshopsModal: Ref<IWorkshop[]> = ref([]);
const foundServicesModal: Ref<IWorkshop[]> = ref([]);
const loading = ref(false);
const typing = ref(false);
const searchAreaButton = ref(false);
const mapCenter = ref({
  lat: 52.248,
  lng: 21.009
});
const mapZoom = ref(13);
const currentWorkshop: Ref<IWorkshop | IWorkshopExtended | null> = ref(null);
const searchInitLoad = ref(false);

// use 
const formWrapper = useFormCreator<IMapSearchFormFields>({
  search: '',
  filters: {
    service: null,
    brand: null
  }
});
const route = useRoute();
const router = useRouter();
const store = useStore();
const { $posthog } = useNuxtApp();


// watch
watch(() => formWrapper.form.value.fields.search, (value) => {
  if (value.length >= 3) {
    typing.value = true;
    debounceSearchServicesOrWorkshops(value);
  } else {
    foundWorkshopsModal.value = [];
  }
});

watch(() => formWrapper.form.value.fields.filters.service, (value) => {
  searchThisArea();
});

watch(() => formWrapper.form.value.fields.filters.brand, (value) => {
  searchThisArea();
});


// methods
const debounceSearchServicesOrWorkshops = debounce(searchServicesOrWorkshops, 500);

const GETworkshops = async (val: string) => {
  const data = await homepageApi.get(`/api/homepage/search-mechanics`, {
    params: {
      'garage_name': val,
      'garage_status': 1
    }
  });
  return data;
};

const GETservices = async (val: string) => {
  const data = await homepageApi.get(`/api/homepage/search-services`, {
    params: {
      'service_name': val,
      'garage_status': 1
    }
  });
  return data;
};

async function searchServicesOrWorkshops(val: string) {
  loading.value = true;
  typing.value = false;

  try {
    const GETservicesResponse = GETservices(val);
    const GETworkshopsResponse = GETworkshops(val);

    const [services, workshops] = await Promise.all([
      (await GETservicesResponse)?.data,
      (await GETworkshopsResponse)?.data
    ]);
    foundServicesModal.value = services;
    foundWorkshopsModal.value = workshops;

    if (!foundServicesModal.value.length || !foundWorkshopsModal.value.length) {
      $posthog()?.capture('no-results', {
        value: val,
        page: 'map',
        type: 'services-or-workshops'
      });
    }
  } catch {

  } finally {
    loading.value = false;
  }
};

const initMap = async (): Promise<void> => {
  const { Map } = await loader.importLibrary('maps');

  map = new Map(document.getElementById('map') as HTMLElement, {
    mapId: '1c6301bdb7363db',
    center: mapCenter.value,
    zoom: mapZoom.value,
    minZoom: 6,
    maxZoom: 20,
    mapTypeControl: false,
    scaleControl: false,
    fullscreenControl: false,
    zoomControl: false,
    streetViewControl: false,
    gestureHandling: 'greedy',
    fullscreenControlOptions: {
      position: 6
    }
  });

  map.addListener("dragend", (e: any) => {
    toggleSearchAreaButton(true);
  });
  map.addListener("zoom_changed", (e: any) => {
    toggleSearchAreaButton(true);
  });
};

const removeAllMarkers = () => {
  // remove all pins
  markers.forEach((marker) => {
    marker.map = null;
  });
  markers = [];
  //
};

const GETsearch = async (lat: number, lng: number, range: number) => {
  loading.value = true;
  currentWorkshop.value = null;

  let params: any = {};

  if (formWrapper.form.value.fields.filters.service) {
    params = {
      service_provided: formWrapper.form.value.fields.filters.service.id
    };
  }

  if (formWrapper.form.value.fields.filters.brand) {
    params = {
      supported_vehicle_brand: formWrapper.form.value.fields.filters.brand.id
    };
  }

  try {
    const { data } = await homepageApi.get(`/api/maps/search-mechanics-geo-distance`, {
      params: {
        'point_lat': lat,
        'point_lng': lng,
        'range': range,
        'garage_status': 1,
        ...params
      }
    });

    foundWorkshops.value = data.map((workshop: IWorkshopExtended, index: number) => {
      return {
        ...workshop,
        index
      };
    });
    setInitialSearchAPICallFlag();
    createMarkers();
  } catch {

  } finally {
    loading.value = false;
  }
};

const toggleSearchAreaButton = (val: boolean) => {
  searchAreaButton.value = val;
};

const setInitialSearchAPICallFlag = () => {
  if (!searchInitLoad.value) {
    searchInitLoad.value = true;
  }
};

const searchThisArea = async (direct?: boolean) => {
  const lat = map.getCenter()?.lat();
  const lng = map.getCenter()?.lng();
  const zoom = map.getZoom();

  if (!lat || !lng || !zoom) return;

  const cordsNorthEast = map.getBounds()?.getNorthEast();
  const cordsSouthWest = map.getBounds()?.getSouthWest();

  const range = convertMapZoomToMeters(cordsNorthEast, cordsSouthWest);

  await GETsearch(lat, lng, range * 1.5);
  toggleSearchAreaButton(false);

  if (direct) {
    $posthog()?.capture('search-this-area', {
      lat,
      lng,
      results: foundWorkshops.value.length,
      zoom,
      search: formWrapper.form.value.fields.search,
      service: formWrapper.form.value.fields.filters.service?.name
    });
  }

};

const setMarkerUI = async (element: google.maps.marker.AdvancedMarkerElement, backgroundColor: string, borderColor: string, glyphColor?: string) => {
  const { PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

  const g = (element.content as Element).getElementsByClassName('custom-gmap-pin')[0];
  const newPinElement = new PinElement({
    background: backgroundColor,
    borderColor: borderColor,
    glyphColor: glyphColor || 'white',
    glyph: g,
    scale: 1.3
  });
  element.content = newPinElement.element;
};

const setBackgroundToAllAlreadyClickedMarkers = () => {
  markers.forEach(el => {
    if (el.className === 'custom-gmap-pin--clicked') {
      setMarkerUI(el, '#7BA9EC', '#ffffff');
    }
  });
};

const createMarkers = async () => {
  const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

  removeAllMarkers();

  foundWorkshops.value.forEach((element: IWorkshopExtended) => {
    if (!element.location?.lat || !element.location?.lat) return;

    const glyph = document.createElement("div");
    glyph.classList.add("custom-gmap-pin");

    if (element.rating_total) {
      glyph.textContent = Number(`${element.rating_total}`).toFixed(1);
      glyph.style.fontSize = "13px";
      glyph.style.fontWeight = "500";
    } else {
      glyph.textContent = '●';
      glyph.style.fontSize = "22px";
    }

    const pinElement = new PinElement({
      background: '#2470e0',
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      glyph: glyph,
      scale: 1.3
    });

    const marker = new AdvancedMarkerElement({
      map: map,
      position: {
        "lat": element.location.lat,
        "lng": element.location.lon
      },
      content: pinElement.element,
      gmpClickable: true
    });

    marker.addListener('click', () => {
      if (!marker) return;

      setBackgroundToAllAlreadyClickedMarkers();
      setMarkerUI(marker, '#ffffff', '#2470E0', '#2470E0');
      marker.classList.add('custom-gmap-pin--clicked');

      currentWorkshop.value = element;
    });

    marker.id = `workshop.${element.id}.pin`;

    markers.push(marker);
  });
};

const createCircle = (lat: number, lng: number, range: number) => {
  if (!circle) {
    circle = new google.maps.Circle({
      strokeColor: "#FF0000",
      strokeOpacity: 0.3,
      strokeWeight: 2,
      fillColor: "#FF0000",
      fillOpacity: 0.1,
      map,
      center: {
        lat: lat,
        lng: lng
      },
      radius: range
    });
  } else {
    circle.setRadius(range);
    circle.setCenter({
      lat, lng
    });
  }
};


const tryToParseInitialService = () => {
  const serviceId = route.query['serviceId'];
  const serviceName = route.query['serviceName'];

  if (serviceId) {
    formWrapper.form.value.fields.filters.service = {
      name: serviceName as string || '',
      id: serviceId as string
    };
  }
};

const tryToParseInitialBrand = () => {
  const brandId = route.query['vehicleBrandId'];
  const brandName = route.query['vehicleBrandName'];

  if (brandId) {
    formWrapper.form.value.fields.filters.brand = {
      name: brandName as string || '',
      id: brandId as string
    };
  }
};

const tryToGetAndParseCoords = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
      if (!!position.coords) {
        mapCenter.value = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        map.setCenter(mapCenter.value);
      }
      searchThisArea();
    }, (error) => {
    }, {
      enableHighAccuracy: true,
      timeout: 5000
    });
  } else {
    tryToGetCoordsFromParams();
  }
};

const tryToGetCoordsFromParams = async () => {
  const routeLat = route.query['lat'];
  const routeLng = route.query['lng'];
  const routeZoom = route.query['zoom'];

  if (routeLat && routeLng) {
    mapCenter.value = {
      lat: Number(routeLat),
      lng: Number(routeLng)
    };
  }
  if (routeZoom) {
    mapZoom.value = Number(routeZoom);
  }

  const listenerHandle = google.maps.event.addListener(map, 'bounds_changed', async () => {
    await searchThisArea();
    google.maps.event.removeListener(listenerHandle);
  });

  map.setCenter(mapCenter.value);
  map.setZoom(mapZoom.value);

};

const showOnMap = (workshop: IWorkshopExtended) => {
  const foundMarker = markers.find(marker => {
    return marker.id === `workshop.${workshop.id}.pin`;
  });

  if (!foundMarker) return;

  setBackgroundToAllAlreadyClickedMarkers();
  setMarkerUI(foundMarker, '#ffffff', '#2470E0', '#2470E0');
  foundMarker.classList.add('custom-gmap-pin--clicked');

  currentWorkshop.value = workshop;
};

const showWorkshop = (workshop: IWorkshopExtended) => {
  let link = '';
  if (formWrapper.form.value.fields.filters.service?.id && useStore().contextAB) {
    link = `/mechanicy/${workshop.slug_city}/${workshop.slug_name}.html?service-context=${formWrapper.form.value.fields.filters.service?.id}`;
  } else {
    link = `/mechanicy/${workshop.slug_city}/${workshop.slug_name}.html`;
  }

  const routeData = router.resolve(link);
  window.open(routeData.href, '_blank');
};

const onMouseOver = (workshop: IWorkshopExtended) => {
  setBackgroundToAllAlreadyClickedMarkers();

  const foundMarker = markers.find(marker => {
    return marker.id === `workshop.${workshop.id}.pin`;
  });

  if (!foundMarker) return;

  setMarkerUI(foundMarker, '#ffffff', '#2470E0', '#2470E0');
  currentWorkshop.value = null;
};

const onMouseLeave = (workshop: IWorkshopExtended) => {
  const foundMarker = markers.find(marker => {
    return marker.id === `workshop.${workshop.id}.pin`;
  });

  if (!foundMarker) return;

  if (foundMarker.className === 'custom-gmap-pin--clicked') {
    setMarkerUI(foundMarker, '#7BA9EC', '#ffffff');
  } else {
    setMarkerUI(foundMarker, '#2470e0', '#ffffff', '#ffffff');
  }
};

onMounted(async () => {
  setTimeout(() => {
    $posthog()?.capture('$pageview');

    // AB TEST
    if ($posthog()?.getFeatureFlag('services-price-list-with-context') === 'context') {
      store.setContextAB();
    }
    //
  }, 0);
  await initMap();
  tryToParseInitialService();
  tryToParseInitialBrand();
  tryToGetCoordsFromParams();
  tryToGetAndParseCoords();
})

</script>

<style lang="scss">
#map {
  width: 100%;
  margin: 0;
  height: 100vh;
  border-radius: 5px;
  height: calc(100vh - 70px);
}

.map {
  @media (min-width: $breakpoint) {
    height: calc(100vh - 70px);
  }

  &__wrapper {
    height: 100%;
    position: relative;

    @media (min-width: $breakpoint) {
      display: flex;
      flex-direction: row-reverse;
      height: calc(100vh - 70px);
    }
  }

  &__list-wrapper {
    position: fixed;
    bottom: 14px;
    left: 6px;
    right: 6px;

    @media (min-width: $breakpoint) {
      flex: 2;
      max-width: 580px;
      position: static;
    }
  }

  &__map-wrapper {
    @media (min-width: $breakpoint) {
      flex: 3;
      position: relative;
    }
  }

  &__search-this-area {
    position: absolute;
    top: 86px;
    left: 0;
    right: 0;
    z-index: 5;
    display: flex;
    justify-content: center;

    i {
      background-color: transparent !important;
    }

    &--down {
      top: 134px;
    }
  }

  .modal__slot {
    @media (min-width: $breakpoint) {
      max-width: 718px !important;
      width: 718px !important;
      height: 660px !important;
      max-height: 660px !important;
    }

    @media (min-width: 1024px) {
      max-width: 950px !important;
      width: 950px !important;
      height: 660px !important;
      max-height: 660px !important;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.custom-gmap-pin {
  transition: all 0.3s ease-in-out;
}

.gm-style iframe+div {
  border: none !important;
}
</style>